import { useEffect, useId, useRef } from 'react';

import Button from 'components/partials/button/button';
import Textarea from 'components/partials/textarea/textarea';

import {
  DECISION_ACTIONS,
  NOTE_MAX_LENGTH,
  NOTE_TEXTAREA_ROWS,
  NOTE_TEXTAREA_STYLES,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import type {
  DecisionActionConfig,
  DecisionActionKey,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';
import { useDecisionPanel } from 'containers/admin/clients/client/tabs-components/critical-populations/hooks/use-decision-panel';

interface DecisionPanelProps {
  clientId: number;
  id: number;
  action: DecisionActionKey;
  onCancel: () => void;
  onDone: () => void;
}

const DecisionPanel = ({
  clientId,
  id,
  action,
  onCancel,
  onDone,
}: DecisionPanelProps) => {
  const titleId = useId();
  const textareaId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const config: DecisionActionConfig = DECISION_ACTIONS[action];
  const { reason, setReason, canConfirm, isPending, handleConfirm } =
    useDecisionPanel({ clientId, id, action, onDone });

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className="flex w-full flex-col gap-2.5 border-t border-navy-100 bg-gray-50 px-4 pb-4 pt-3"
      data-testid={`decision-panel-${action}`}
    >
      <div className="flex flex-col gap-px">
        <p id={titleId} className="text-base font-semibold text-gray-700">
          {config.title}
        </p>
        <p className="text-sm text-gray-700">{config.description}</p>
      </div>
      <Textarea
        ref={textareaRef}
        label={config.field.label}
        labelFor={textareaId}
        required
        helperText={config.field.helper}
        helperTextPosition="below"
        maxLength={NOTE_MAX_LENGTH}
        showCharCount={false}
        textareaStyles={NOTE_TEXTAREA_STYLES}
        rows={NOTE_TEXTAREA_ROWS}
        value={reason}
        onChange={setReason}
        data-testid={`decision-panel-${action}-reason`}
      />
      <div className="flex gap-3">
        <Button
          variant={config.confirmVariant}
          size="sm"
          disabled={!canConfirm}
          loading={isPending}
          onClick={handleConfirm}
          data-testid={`decision-panel-${action}-confirm`}
        >
          {config.confirmLabel}
        </Button>
        <Button
          variant="neutral-outlined"
          size="sm"
          onClick={onCancel}
          data-testid={`decision-panel-${action}-cancel`}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default DecisionPanel;
